'use client'

import { useIsMac, formatKeyToken } from '@/lib/utils/shortcutKeys'

// Glyphs for non-modifier keys. Modifiers (Cmd/Alt/Ctrl/Shift) are resolved by
// `formatKeyToken` — macOS glyphs vs. spelled-out names per platform.
const KEY_GLYPHS: Record<string, string> = {
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  Delete: '⌫',
  Escape: 'Esc',
  Space: '␣',
}

function keyLabel(token: string, isMac: boolean): string {
  const mod = formatKeyToken(token, isMac)
  if (mod !== token) return mod // modifier key
  return KEY_GLYPHS[token] ?? token
}

function Key({ label }: { label: string }) {
  return (
    <kbd className="inline-flex min-w-[22px] items-center justify-center rounded border border-border/50 bg-secondary/80 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
      {label}
    </kbd>
  )
}

function ComboGroup({ combo, isMac }: { combo: string; isMac: boolean }) {
  if (combo.includes('+') && combo.length > 1) {
    const parts = combo.split('+')
    return (
      <span className="inline-flex items-center gap-0.5">
        {parts.map((p, i) => (
          <Key key={i} label={keyLabel(p, isMac)} />
        ))}
      </span>
    )
  }

  if (combo.includes('/')) {
    const parts = combo.split('/')
    return (
      <span className="inline-flex items-center gap-1">
        {parts.map((p, i) => (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 && <span className="text-[10px] text-muted-foreground/30">/</span>}
            <Key label={keyLabel(p, isMac)} />
          </span>
        ))}
      </span>
    )
  }

  if (/^\d+-\d+$/.test(combo)) {
    const [start, end] = combo.split('-')
    return (
      <span className="inline-flex items-center gap-1">
        <Key label={start} />
        {/* eslint-disable-next-line i18next/no-literal-string -- decorative */}
        <span className="text-[10px] text-muted-foreground/30">–</span>
        <Key label={end} />
      </span>
    )
  }

  return <Key label={keyLabel(combo, isMac)} />
}

/**
 * Render a shortcut string (e.g. `Cmd+Shift+Z` or `J / K / L`) as styled key
 * caps, formatted for the visitor's OS. Client component so the docs page can
 * stay server-rendered while the modifier glyphs adapt per platform.
 */
export function ShortcutKeys({ shortcut }: { shortcut: string }) {
  const isMac = useIsMac()
  const groups = shortcut.split(' / ')

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      {groups.map((group, gi) => (
        <span key={gi} className="inline-flex items-center gap-1.5">
          {gi > 0 && <span className="text-[10px] text-muted-foreground/30">/</span>}
          <ComboGroup combo={group} isMac={isMac} />
        </span>
      ))}
    </span>
  )
}
